import _ from 'underscore';
import { useContext, useEffect, useMemo } from 'react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import {
  useCustomerLevelVariables,
  useAccountLevelVariables,
  usePostponeReadVariables,
} from '@bingads-webui-campaign-react/basic-context-utils';
import { getTimezoneI18nString } from '@bingads-webui-campaign-react/help';
import { CurrencyUtils } from '@bingads-webui-clientcenter/entity-utils';
import { usePromisify, FetchPolicy } from '@bingads-webui-react/overreact';
import {
  useCountries,
  useAccount,
  useTimeZones,
  useCurrencyCountriesMappings,
  useCurrencies,
  useSubGeographies,
  useCountryTimeZonesMapping,
} from '@bingads-webui/overreact-cc';

import { useGetSupportedCountryList } from './use-get-supported-country-list';

function convertTimeZonesForCcForm(i18n, timeZones) {
  return _.map(timeZones, item => ({
    Value: item.id,
    Text: i18n.getString(getTimezoneI18nString(item.id)),
  }));
}

export const useLoadAccountInfo = ({ countryCode, timezonePrefillEnabled }) => {
  const i18n = useContext(I18nContext);

  const customerLevelVariables = useCustomerLevelVariables();
  const accountLevelVariables = useAccountLevelVariables();
  const postponeReadConfig = usePostponeReadVariables();

  const [{ data: countries }] = useCountries(customerLevelVariables);
  const [{ data: account }] = useAccount(accountLevelVariables);
  const [{ data: tZones }] = useTimeZones(customerLevelVariables);
  const [{ data: currencyCountriesMappings }] = useCurrencyCountriesMappings(customerLevelVariables);
  const [{ data: currencies }] = useCurrencies(customerLevelVariables);
  const [{ data: countryTimeZonesMapping }, { read: readCountryTimeZonesMapping }]
    = useCountryTimeZonesMapping(customerLevelVariables, postponeReadConfig);
  const [{ data: statesOrProvinces }, { read: readStatesOrProvinces }]
    = useSubGeographies(customerLevelVariables, postponeReadConfig);
  const [readStatesOrProvincesAsync, readCountryTimeZonesMappingAsync]
    = usePromisify([readStatesOrProvinces, readCountryTimeZonesMapping]);

  const { supportedCountryList, getSupportedCountryList } = useGetSupportedCountryList();

  const isAccountInfoLoaded = account && countries && tZones
    && currencyCountriesMappings && currencies && statesOrProvinces
    && (!timezonePrefillEnabled || (timezonePrefillEnabled && countryTimeZonesMapping));

  const prefillTimezone = _.isEmpty(countryTimeZonesMapping) ? undefined :
    _.first(_.get(_.findWhere(countryTimeZonesMapping, { country: countryCode }), 'timeZones'));

  const preferredTimeZoneCode = timezonePrefillEnabled ? prefillTimezone : _.get(account, ['TimeZone']);

  const timeZones = useMemo(() => (
    convertTimeZonesForCcForm(i18n, tZones)
  ), [i18n, tZones]);

  const sortedCurrencies = useMemo(() => {
    const sorted = CurrencyUtils.sortCurrencies(_.pluck(currencies, 'value'));
    return sorted;
  }, [currencies]);

  useEffect(() => {
    if (_.isEmpty(supportedCountryList)) {
      getSupportedCountryList();
    }
  }, [getSupportedCountryList, supportedCountryList]);

  useEffect(() => {
    if (_.isEmpty(statesOrProvinces) && countryCode) {
      readStatesOrProvincesAsync({
        variables: {
          countryCode,
          options: {
            fetchPolicy: FetchPolicy.NetworkOnly,
          },
        },
      });
    }
  }, [statesOrProvinces, readStatesOrProvincesAsync, countryCode]);

  useEffect(() => {
    if (timezonePrefillEnabled && _.isEmpty(countryTimeZonesMapping)) {
      readCountryTimeZonesMappingAsync();
    }
  }, [countryTimeZonesMapping, readCountryTimeZonesMappingAsync, timezonePrefillEnabled]);

  return {
    account,
    countries,
    timeZones,
    currencies: sortedCurrencies,
    preferredTimeZoneCode,
    statesOrProvinces,
    supportedCountryList,
    isAccountInfoLoaded,
  };
};
